import { useEffect, useState } from 'react';
import {
  BoxV2 as Box,
  FilterType,
  Table,
  TableFilter,
  Flex,
  useTable,
  useFilters,
  usePermission,
} from 'portal-commons';

import {
  getRcsBillingCategoryOptions,
  getBrandNameAutocompleteOptions,
  getDcaOptions,
  getRcsUseCaseOptions,
  getRejectionCategoryOptions,
} from '@/apis/services';
import RcsCampaignsByPrimaryDcasListingRow from '../components/RcsCampaignsByPrimaryDcasListingRow';
import { MAX_EVENT_RECORDS } from '../../events/constants';
import { FilterToggleButton, Loader } from '@/shared_elements';
import RcsCampaignActionModal from '../components/RcsCampaignActionModal';
import { campaignController } from '@/apis/core';
import { CampaignListItem } from '@/apis/core/types';
import { SelectItem } from '@/types';

const FILTER_CONFIGS = {
  campaignUid: {
    type: FilterType.Text,
    label: 'Campaign ID',
    placeholder: 'Type Campaign ID',
  },
  // displayName: {
  //   type: FilterType.Text,
  //   label: 'Display Name',
  //   placeholder: 'Type Display Name',
  //   suggestions: [],
  // },
  brandUid: {
    type: FilterType.Text,
    label: 'Brand ID',
    placeholder: 'Type Brand ID',
  },
  brandName: {
    type: FilterType.Text,
    label: 'Brand DBA Name',
    placeholder: 'Type Brand DBA Name',
    suggestions: [],
  },
  billingCategory: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Billing Category',
  },
  usecase: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Use Case',
  },
  primaryDcaUid: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Primary DCA',
    multiple: true,
    width: 300,
  },
  status: {
    type: FilterType.Dropdown,
    options: [
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Deactivated', value: 'EXPIRED' },
    ],
    label: 'TCR Status',
    width: 220,
  },
  secondarySharingStatus: {
    type: FilterType.Dropdown,
    options: [
      { label: 'Accepted', value: 'ACCEPTED' },
      { label: 'Pending', value: 'PENDING' },
    ],
    label: 'Acceptance Status',
    multiple: true,
    width: 260,
  },
};

const TABLE_HEADS = [
  { id: 'campaignUid', label: 'Campaign ID', sortable: true },
  // { id: 'displayName', label: 'Display Name', sortable: false },
  { id: 'brandUid', label: 'Brand ID', sortable: true },
  { id: 'brandName', label: 'Brand DBA Name', sortable: false },
  { id: 'billingCategory', label: 'Billing Category', sortable: false },
  { id: 'usecase', label: 'Use Case', sortable: true },
  { id: 'primaryDcaName', label: 'Primary DCA', sortable: false },
  { id: 'electedMno', label: 'Elected for MNO', sortable: false },
  { id: 'sharedOnDate', label: 'Shared On', sortable: true },
  { id: 'status', label: 'TCR Status', sortable: false },
  {
    id: 'secondarySharingStatus',
    label: 'Acceptance Status',
    sortable: false,
  },
];

const FILTER_LOADERS = {
  suggestion: {
    // todo: add displayName suggestion loader
    brandName: {
      minLength: 2,
      load: (val: string) => getBrandNameAutocompleteOptions(val),
    },
  },
};

const RcsCampaignsByPrimaryDcas = () => {
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [selectedCampaign, setSelectedCampaign] =
    useState<CampaignListItem | null>(null);
  const [actionType, setActionType] = useState<'accept' | 'decline' | null>(
    null
  );
  const [rejectionCategoryOptions, setRejectionCategoryOptions] =
    useState<SelectItem[]>();
  const toggleFilterVisibility = () => setIsFilterVisible((prev) => !prev);
  const { hasPermission } = usePermission();

  const {
    appliedFilters,
    additionalFilters,
    candidateFilters,
    configs: filterConfigs,
    handleEdit,
    handleApply,
    setOptions: setFilterOptions,
  } = useFilters({
    configs: FILTER_CONFIGS,
    loaders: FILTER_LOADERS,
  });

  const fetchCampaigns = async () => {
    const response = await campaignController.getSecondaryDcaCampaigns({
      secondarySharingStatus: 'PENDING,ACCEPTED',
      ...getQueries(),
      channelId: 'RCS',
    });
    return {
      data: response?.records || [],
      rowsPerPage: response?.recordsPerPage || 10,
      total: response?.totalRecords || 0,
      page: response?.page || 1,
    };
  };

  const getQueries = () => {
    return {
      ...table.filter,
      ...appliedFilters,
      ...additionalFilters,
    };
  };

  const table = useTable<CampaignListItem>(fetchCampaigns, {
    cappingTotal: MAX_EVENT_RECORDS,
    deps: [appliedFilters, additionalFilters],
    defaultFilter: {
      sortField: 'sharedOnDate',
      ascendingOrder: false,
    },
  });

  const fetchFilterOptions = async () => {
    const results = await Promise.allSettled([
      getRcsBillingCategoryOptions(),
      getRcsUseCaseOptions(),
      getDcaOptions(),
    ]);

    results.forEach((r, index) => {
      if (r.status === 'fulfilled') {
        const filterKey = {
          0: 'billingCategory',
          1: 'usecase',
          2: 'primaryDcaUid',
        };
        const key = filterKey[index];
        key && setFilterOptions(key, r.value || []);
      }
    });
  };

  useEffect(() => {
    fetchFilterOptions();
    getRejectionCategoryOptions().then((options) =>
      setRejectionCategoryOptions(options)
    );
  }, []);

  const handleRowAction = ({ type, campaign }) => {
    setActionType(type);
    setSelectedCampaign(campaign);
  };

  const handleRowActionComplete = () => {
    setSelectedCampaign(null);
    setActionType(null);
    table.reload();
  };

  if (table.pageLoading) {
    return <Loader />;
  }

  return (
    <section data-testid="rcsCampaignsByPrimaryDcas">
      <Flex
        sx={{
          alignItems: 'center',
          mb: 'xs',
          fontWeight: 600,
          fontSize: 'H600',
          lineHeight: '32px',
          color: 't.black80',
        }}
      >
        <span>
          {!table.pageLoading && table.total !== 0 ? `${table.total} ` : ''} RCS
          Campaigns Shared by DCAs
        </span>
      </Flex>
      <Box sx={{ mt: 'xs', mb: 's' }}>
        <FilterToggleButton
          visible={isFilterVisible}
          onClick={toggleFilterVisibility}
        />
      </Box>
      {isFilterVisible && (
        <Box sx={{ '.MuiAutocomplete-root': { width: 'auto' }, my: 's' }}>
          <TableFilter
            configs={filterConfigs}
            candidateValues={candidateFilters}
            appliedValues={appliedFilters}
            onCandidateValuesChange={handleEdit}
            onAppliedValuesChange={handleApply}
            data-testid="rcsCampaignsByPrimaryDcasTableFilter"
          />
        </Box>
      )}
      <Table
        testId="rcsCampaignByPrimaryDcasTable"
        headRows={TABLE_HEADS}
        emptyState="no campaigns to view"
        loading={table.loading}
        tableData={table.tableData.map((record) => (
          <RcsCampaignsByPrimaryDcasListingRow
            key={record.uid}
            campaign={record}
            onActionClick={handleRowAction}
            permissions={{
              accept: hasPermission(
                'rcsCampaignByDcaList.acceptCampaign'
              ) as boolean,
              decline: hasPermission(
                'rcsCampaignByDcaList.declineCampaign'
              ) as boolean,
            }}
          />
        ))}
        handleChangePage={table.handleChangePage}
        createSortHandler={table.handleSorting}
        filter={table.filter}
        pagination={table.pagination}
        headerTextTransform="none"
      />
      {selectedCampaign && actionType && (
        <RcsCampaignActionModal
          campaignUid={selectedCampaign.uid}
          open={!!actionType}
          type={actionType}
          decliningCampaign={{
            campaignUid: selectedCampaign.uid,
            brandUid: selectedCampaign.brandUid,
            downstreamCnpName: selectedCampaign.primaryDcaName,
          }}
          rejectionCategoryOptions={rejectionCategoryOptions}
          onCancel={() => {
            setSelectedCampaign(null);
            setActionType(null);
          }}
          onComplete={handleRowActionComplete}
          dca2
        />
      )}
    </section>
  );
};

export default RcsCampaignsByPrimaryDcas;
